<template>
  <div>
    <div
      v-if="showFallback"
      class="crisp-fallback-bubble"
      @click="showModal = true"
    >
      <b-icon icon="chat-dots-fill" font-scale="1.4"></b-icon>
    </div>

    <b-modal
      v-model="showModal"
      :title="$t('chatFallback.title')"
      centered
      hide-footer
      size="sm"
      body-class="p-4"
    >
      <p class="mb-3">{{ $t('chatFallback.description') }}</p>
      <b-button
        variant="success"
        block
        class="mb-2"
        @click="acceptCookies"
      >
        <b-icon icon="chat-dots" class="mr-1"></b-icon>
        {{ $t('chatFallback.enableChat') }}
      </b-button>
      <b-button
        variant="outline-secondary"
        block
        href="mailto:support@webmag.io"
        @click="showModal = false"
      >
        <b-icon icon="envelope" class="mr-1"></b-icon>
        {{ $t('chatFallback.emailUs') }}
      </b-button>
    </b-modal>
  </div>
</template>

<script>
const LOG_PREFIX = '[CrispFallback]';
const MAX_RETRIES = 1;
const POLL_INTERVAL_MS = 200;
const TIMEOUT_MS = 5000;
const BACKGROUND_POLL_MS = 3000;
const VISIBILITY_TIMEOUT_MS = 5000;
const VISIBILITY_POLL_MS = 500;
const CRISP_CDN_SRC = 'https://client.crisp.chat/l.js';

export default {
  name: 'useCrisp',
  props: {
    email: {
      type: String,
      default: null,
    },
    groupName: {
      type: String,
      default: null,
    },
    userRegistered: {
      type: Boolean,
      default: null,
    },
  },
  data() {
    return {
      crispReady: false,
      scriptLoaded: false,
      crispCheckInterval: null,
      crispTimeout: null,
      visibilityCheckInterval: null,
      retryCount: 0,
      showFallback: false,
      showModal: false,
      backgroundPollInterval: null,
    };
  },
  mounted() {
    console.log(LOG_PREFIX, 'mounted, starting initializeCrisp');
    this.initializeCrisp();
    window.addEventListener('CCM19.consentChange', this.onConsentChange);
  },
  beforeDestroy() {
    this.clearTimers();
    this.clearBackgroundPoll();
    this.clearVisibilityCheck();
    this.crispReady = false;
    window.removeEventListener('CCM19.consentChange', this.onConsentChange);
  },
  watch: {
    email(newEmail) {
      if (this.crispReady && newEmail) {
        this.updateCrispEmail(newEmail);
      }
    },
    groupName(newGroupName) {
      if (this.crispReady && newGroupName) {
        this.updateCrispGroupName(newGroupName);
      }
    },
  },
  methods: {
    clearTimers() {
      if (this.crispCheckInterval) {
        clearInterval(this.crispCheckInterval);
        this.crispCheckInterval = null;
      }
      if (this.crispTimeout) {
        clearTimeout(this.crispTimeout);
        this.crispTimeout = null;
      }
    },
    clearBackgroundPoll() {
      if (this.backgroundPollInterval) {
        clearInterval(this.backgroundPollInterval);
        this.backgroundPollInterval = null;
      }
    },
    clearVisibilityCheck() {
      if (this.visibilityCheckInterval) {
        clearInterval(this.visibilityCheckInterval);
        this.visibilityCheckInterval = null;
      }
    },
    isCrispReady() {
      const ready = window.$crisp && typeof window.$crisp.is === 'function';
      return ready;
    },
    isCrispVisible() {
      const el = document.querySelector('.crisp-client');
      return !!el;
    },
    startBackgroundPoll() {
      console.log(LOG_PREFIX, 'starting background poll (every', BACKGROUND_POLL_MS, 'ms)');
      this.clearBackgroundPoll();
      this.backgroundPollInterval = setInterval(() => {
        const visible = this.isCrispVisible();
        if (visible) {
          console.log(LOG_PREFIX, 'background poll: Crisp became visible, hiding fallback');
          this.clearBackgroundPoll();
          this.showFallback = false;
          this.showModal = false;
        }
      }, BACKGROUND_POLL_MS);
    },
    onCrispApiReady() {
      console.log(LOG_PREFIX, 'Crisp API ready ($crisp.is is a function). Verifying widget visibility...');
      this.clearTimers();
      this.crispReady = true;
      this.configureCrisp();
      this.verifyCrispVisible();
    },
    verifyCrispVisible() {
      if (this.isCrispVisible()) {
        console.log(LOG_PREFIX, 'verifyCrispVisible: .crisp-client found immediately, Crisp is working');
        this.showFallback = false;
        this.showModal = false;
        return;
      }

      console.log(LOG_PREFIX, 'verifyCrispVisible: .crisp-client NOT found yet, polling for', VISIBILITY_TIMEOUT_MS, 'ms');
      this.clearVisibilityCheck();
      let elapsed = 0;

      this.visibilityCheckInterval = setInterval(() => {
        elapsed += VISIBILITY_POLL_MS;

        if (this.isCrispVisible()) {
          console.log(LOG_PREFIX, 'verifyCrispVisible: .crisp-client appeared after', elapsed, 'ms');
          this.clearVisibilityCheck();
          this.showFallback = false;
          this.showModal = false;
          return;
        }

        if (elapsed >= VISIBILITY_TIMEOUT_MS) {
          console.log(LOG_PREFIX, 'verifyCrispVisible: .crisp-client never appeared after', VISIBILITY_TIMEOUT_MS, 'ms -> showing fallback bubble');
          this.clearVisibilityCheck();
          this.showFallback = true;
          this.startBackgroundPoll();
        }
      }, VISIBILITY_POLL_MS);
    },
    onConsentChange() {
      console.log(LOG_PREFIX, 'CCM19.consentChange event fired, showFallback:', this.showFallback);
      if (this.showFallback) {
        this.retryLoadCrisp();
      }
    },
    retryLoadCrisp() {
      console.log(LOG_PREFIX, 'retryLoadCrisp: resetting and reinitializing Crisp from scratch');
      this.crispReady = false;
      this.retryCount = 0;
      this.clearTimers();
      this.clearVisibilityCheck();
      this.clearBackgroundPoll();

      const cdnScript = document.querySelector(`script[src="${CRISP_CDN_SRC}"]`);
      if (cdnScript) {
        cdnScript.remove();
      }
      const loaderScript = document.querySelector('script[src="/crisp.js"]');
      if (loaderScript) {
        loaderScript.remove();
      }

      window.CRISP_WEBSITE_ID = undefined;
      if (!window.$crisp || typeof window.$crisp.is !== 'function') {
        window.$crisp = [];
      }

      this.initializeCrisp();
    },
    acceptCookies() {
      console.log(LOG_PREFIX, 'acceptCookies: opening CCM widget. CCM available:', !!(window.CCM && typeof window.CCM.openWidget === 'function'));
      this.showModal = false;
      if (window.CCM && typeof window.CCM.openWidget === 'function') {
        window.CCM.openWidget();
      }
    },
    initializeCrisp() {
      const alreadyReady = this.isCrispReady();
      console.log(LOG_PREFIX, 'initializeCrisp: isCrispReady:', alreadyReady,
        '| window.$crisp:', typeof window.$crisp,
        '| $crisp.is:', window.$crisp && typeof window.$crisp.is);

      if (alreadyReady) {
        this.onCrispApiReady();
        return;
      }

      const hasLoaderScript = document.querySelector('script[src="/crisp.js"]');
      const hasCdnScript = document.querySelector(`script[src="${CRISP_CDN_SRC}"]`);
      console.log(LOG_PREFIX, 'initializeCrisp: hasLoaderScript:', !!hasLoaderScript, '| hasCdnScript:', !!hasCdnScript);

      if (hasLoaderScript || hasCdnScript) {
        console.log(LOG_PREFIX, 'initializeCrisp: script tag exists, waiting for Crisp API');
        this.waitForCrisp();
        return;
      }

      if (!window.$crisp) {
        window.$crisp = [];
      }

      console.log(LOG_PREFIX, 'initializeCrisp: injecting /crisp.js script tag');
      const crispJs = document.createElement('script');
      crispJs.setAttribute('src', '/crisp.js');
      crispJs.setAttribute('async', 'true');

      crispJs.addEventListener('load', () => {
        console.log(LOG_PREFIX, 'initializeCrisp: /crisp.js loaded successfully');
        this.scriptLoaded = true;
        this.waitForCrisp();
      });

      crispJs.addEventListener('error', () => {
        console.error(LOG_PREFIX, 'initializeCrisp: /crisp.js failed to load');
        this.handleRetry();
      });

      document.head.appendChild(crispJs);
    },
    waitForCrisp() {
      console.log(LOG_PREFIX, 'waitForCrisp: polling every', POLL_INTERVAL_MS, 'ms, timeout:', TIMEOUT_MS, 'ms');
      this.clearTimers();

      this.crispCheckInterval = setInterval(() => {
        if (this.isCrispReady()) {
          this.clearTimers();
          this.onCrispApiReady();
        }
      }, POLL_INTERVAL_MS);

      this.crispTimeout = setTimeout(() => {
        console.log(LOG_PREFIX, 'waitForCrisp: TIMEOUT after', TIMEOUT_MS, 'ms. crispReady:', this.crispReady);
        if (this.crispCheckInterval) {
          clearInterval(this.crispCheckInterval);
          this.crispCheckInterval = null;
        }
        if (!this.crispReady) {
          this.handleRetry();
        }
      }, TIMEOUT_MS);
    },
    handleRetry() {
      if (this.retryCount >= MAX_RETRIES) {
        console.warn(LOG_PREFIX, `handleRetry: giving up after ${MAX_RETRIES} retries -> showing fallback`);
        this.showFallback = true;
        this.startBackgroundPoll();
        return;
      }
      this.retryCount += 1;
      console.log(LOG_PREFIX, 'handleRetry: attempt', this.retryCount, 'of', MAX_RETRIES);

      const cdnScript = document.querySelector(`script[src="${CRISP_CDN_SRC}"]`);
      if (cdnScript) {
        cdnScript.remove();
      }

      window.CRISP_WEBSITE_ID = undefined;

      if (!window.$crisp || typeof window.$crisp.is !== 'function') {
        window.$crisp = [];
      }

      window.CRISP_WEBSITE_ID = '3e65dc14-0f6b-4bf6-8637-c3001bc203c5';
      const s = document.createElement('script');
      s.src = CRISP_CDN_SRC;
      s.async = true;
      document.head.appendChild(s);

      this.waitForCrisp();
    },
    configureCrisp() {
      if (!this.isCrispReady()) {
        return;
      }

      if (this.email) {
        this.updateCrispEmail(this.email);
      }

      if (this.groupName) {
        this.updateCrispGroupName(this.groupName);
      }
    },
    updateCrispEmail(email) {
      if (this.isCrispReady()) {
        try {
          window.$crisp.push(['set', 'user:email', [email]]);
        } catch (error) {
          console.error('Error setting Crisp email:', error);
        }
      }
    },
    updateCrispGroupName(groupName) {
      if (this.isCrispReady()) {
        try {
          window.$crisp.push(['set', 'session:data', [['last_group', groupName]]]);
        } catch (error) {
          console.error('Error setting Crisp group name:', error);
        }
      }
    },
  },
};
</script>

<style scoped>
.crisp-fallback-bubble {
  position: fixed;
  bottom: 18px;
  right: 18px;
  z-index: 999999;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: #4FB376;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.crisp-fallback-bubble:hover {
  transform: scale(1.08);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}
</style>
